import { Session } from '@benzinga/session';
import { Subscribable, SubscribableEventType, Subscription } from '@benzinga/subscribable';

import { QuoteFieldsStore } from './store';
import { oneRequestAtATime } from '@benzinga/utils';
import { SafePromise } from '@benzinga/safe-await';
import { QuotesV3FieldsEnvironment } from './environment';

import { DataFieldIngress, FullDataField } from './entities';
import { QuotesV3FieldsRequest } from './request';

import quoteMapping from './quote-mapping.json';
const indexToField = new Map<number, (typeof quoteMapping)[0]['name']>(
  quoteMapping.map(field => [field.index as number, field.name as (typeof quoteMapping)[0]['name']] as const),
);

export class QuotesV3FieldsManager extends Subscribable<
  SubscribableEventType<QuoteFieldsStore> | SubscribableEventType<QuotesV3FieldsRequest>
> {
  private store: QuoteFieldsStore;
  private storeSubscription?: Subscription<QuoteFieldsStore>;
  private request: QuotesV3FieldsRequest;
  private requestSubscription?: Subscription<QuotesV3FieldsRequest>;

  private fetchScannerDefs = oneRequestAtATime(async (): SafePromise<FullDataField[] | undefined> => {
    const cachedScannerDefs = this.store.getFields();
    if (cachedScannerDefs && cachedScannerDefs.length > 0) return { ok: cachedScannerDefs };
    const defs = await this.request.getScannerDefs();
    if (defs.err) {
      const dataFieldsJson = await import('./dataFields.json').then(module => module.default);
      this.store.updateScannersDefs(dataFieldsJson as unknown as DataFieldIngress);
    } else {
      this.store.updateScannersDefs(defs.ok);
    }
    return { ok: this.store.getFields() };
  });

  constructor(session: Session) {
    super();

    this.store = new QuoteFieldsStore();
    this.request = new QuotesV3FieldsRequest(session);
    this.getScannerDefs();
  }

  public static readonly getName = () => QuotesV3FieldsEnvironment.getName();

  public indexToField = (index: number): (typeof quoteMapping)[0]['name'] | undefined => indexToField.get(index);

  public getCachedScannerDefs = (): FullDataField[] | undefined => this.store.getFields();

  public getScannerDefs = async (force?: boolean): SafePromise<FullDataField[] | undefined> => {
    if (this.store.shouldWeFetchFields() || force) {
      return this.fetchScannerDefs();
    } else {
      return { ok: this.store.getFields() };
    }
  };

  public getFieldIds = (): Set<string> => this.store.getFieldIds();

  public getFilterableFields = (): FullDataField[] | undefined => this.store.getFilterableFields();
  public getColumableFields = (): FullDataField[] | undefined => this.store.getColumableFields();
  public getNumericColumableFields = (): FullDataField[] | undefined => this.store.getNumericColumableDefs();
  public getScannerDefFromId = (name: string): FullDataField | undefined => this.store.getScannerDefFromId(name);

  public getColumnScannerDefForField = (field: FullDataField) => {
    switch (field.name) {
      case 'gicsSector':
        return this.getScannerDefFromId('gicsSectorName');
      default:
        return field;
    }
  };

  protected override onFirstSubscription(): void {
    this.storeSubscription = this.store.listen(event => this.dispatch(event));
    this.requestSubscription = this.request.listen(event => this.dispatch(event));
  }

  protected override onZeroSubscriptions(): void {
    this.storeSubscription?.unsubscribe();
    this.requestSubscription?.unsubscribe();
  }
}
